import { describeError, useApi } from '@classgo/app-kit';
import type { TimetableBlock } from '@classgo/shared';
import { Button, Card, EmptyState, Modal, Select, Tabs, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { listQuery } from '../../academic/api';
import type { SchoolYearView } from '../../academic/types';
import { ConfirmDialog } from '../../components/confirm';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';
import { timetableApi, timetableQueries } from '../../timetable/api';
import { BlockForm } from './block-form';
import { CopyForm } from './copy-form';
import { TimetableGrid } from './grid';
import { DEFAULT_SLOT, teacherName, weekdayOf, yearLabel } from './support';
import type { CopyOutcome, GroupDialog, Slot, TimetableViewKind } from './types';

/**
 * The year the screen opens on: the active one, else the planned one that starts first (the
 * timetable is built before the year starts), else the most recent.
 */
const defaultYear = (years: readonly SchoolYearView[]): SchoolYearView | undefined =>
  years.find((year) => year.status === 'active') ??
  years
    .filter((year) => year.status === 'planned')
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))[0] ??
  [...years].sort((a, b) => b.startsOn.localeCompare(a.startsOn))[0];

const Loading = () => {
  const t = useT();
  return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
};

const Failed = ({ error }: { error: unknown }) => {
  const t = useT();
  return <EmptyState title={t('entity.error.title')} description={describeError(t, error)} />;
};

/**
 * What a copy did, above the grid until dismissed (the only feedback of a copy, so the toast
 * does not repeat it): how many blocks came over, which ones lost their teacher or classroom
 * because they were busy, and how many were skipped.
 */
const CopySummary = ({ outcome, onDismiss }: { outcome: CopyOutcome; onDismiss: () => void }) => {
  const t = useT();
  const { result, from } = outcome;
  const named = (blockId: string) => result.view.blocks.find((block) => block.id === blockId);
  return (
    <div
      role="status"
      className="rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
    >
      <p className="font-medium">{t('timetable.copy.done', { copied: result.copied, from })}</p>
      {result.cleared.length > 0 ? (
        <>
          <p className="mt-1">{t('timetable.copy.cleared', { count: result.cleared.length })}</p>
          <ul className="mt-1 list-disc pl-5">
            {result.cleared.map((item) => {
              const block = named(item.blockId);
              return (
                <li key={`${item.blockId}-${item.field}`}>
                  {block
                    ? t(`timetable.copy.cleared.${item.field}`, {
                        subject: block.subjectName,
                        day: t(`timetable.day.${weekdayOf(block.weekday)}`).toLowerCase(),
                        start: block.startsAt,
                        end: block.endsAt,
                      })
                    : item.blockId}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      {result.skipped.length > 0 ? (
        <p className="mt-1">{t('timetable.copy.skipped', { count: result.skipped.length })}</p>
      ) : null}
      <div className="mt-2">
        <Button variant="secondary" size="sm" onClick={onDismiss}>
          {t('timetable.copy.dismiss')}
        </Button>
      </div>
    </div>
  );
};

/**
 * A group's week of the year: the grid, and with `timetable:write` the editor over it (add from
 * a cell or the toolbar, open a block to move or remove it, copy another group's week).
 */
const GroupWeek = ({ yearId, canWrite }: { yearId: string; canWrite: boolean }) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const toast = useToast();
  const { session, sede } = useTenant();
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const groupItems = (groups.data?.items ?? []).filter(
    (group) => group.schoolYearId === yearId && group.sedeId === sede.id,
  );
  const [groupId, setGroupId] = useState('');
  const group = groupItems.find((candidate) => candidate.id === groupId) ?? groupItems[0];
  const week = useQuery(timetableQueries.group(api, group?.id ?? ''));
  const blocks = week.data?.blocks ?? [];
  const subjects = useQuery({ ...listQuery(api, 'subjects'), enabled: canWrite });
  const teachers = useQuery({
    ...recordQueries.teachers(api),
    enabled: canWrite && can(session, 'teachers:read'),
  });
  const classrooms = useQuery({
    ...listQuery(api, 'classrooms', { sedeId: sede.id }),
    enabled: canWrite && can(session, 'classrooms:read'),
  });
  const years = useQuery({ ...listQuery(api, 'years'), enabled: canWrite });
  const [dialog, setDialog] = useState<GroupDialog | null>(null);
  const [highlighted, setHighlighted] = useState<ReadonlySet<string>>(new Set());
  const [outcome, setOutcome] = useState<CopyOutcome | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const refresh = () => queries.invalidateQueries({ queryKey: ['school', 'timetable'] });
  const close = () => {
    setDialog(null);
    setDeleteError(null);
  };
  const remove = useMutation({
    mutationFn: (block: TimetableBlock) => timetableApi(api).remove(block.id),
    onSuccess: async () => {
      close();
      setHighlighted(new Set());
      toast.show({ tone: 'success', title: t('timetable.deleted') });
      await refresh();
    },
    onError: (thrown) => setDeleteError(describeError(t, thrown)),
  });
  const words = (block: TimetableBlock) => ({
    subject: block.subjectName,
    day: t(`timetable.day.${weekdayOf(block.weekday)}`).toLowerCase(),
    start: block.startsAt,
    end: block.endsAt,
  });
  const editing = dialog?.kind === 'edit' ? dialog.block : null;
  const formOpen = dialog?.kind === 'add' || dialog?.kind === 'edit';
  const slot = dialog?.kind === 'add' ? dialog.slot : DEFAULT_SLOT;

  if (groups.isError) return <Failed error={groups.error} />;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,20rem)_1fr] sm:items-end">
        <Select
          label={t('timetable.filter.group')}
          value={group?.id ?? ''}
          onChange={(event) => setGroupId(event.target.value)}
          options={groupItems.map((item) => ({ value: item.id, label: item.name }))}
          placeholder={groupItems.length === 0 ? t('timetable.noGroup') : undefined}
        />
        {canWrite && group ? (
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <Button onClick={() => setDialog({ kind: 'add', slot: DEFAULT_SLOT })}>
              {t('timetable.add')}
            </Button>
            <Button variant="secondary" onClick={() => setDialog({ kind: 'copy' })}>
              {t('timetable.copy')}
            </Button>
          </div>
        ) : null}
      </div>
      {canWrite && group && blocks.length > 0 ? (
        <p className="text-sm text-gray-600">{t('timetable.hint')}</p>
      ) : null}
      {outcome ? <CopySummary outcome={outcome} onDismiss={() => setOutcome(null)} /> : null}
      {!group ? (
        groups.isPending ? (
          <Loading />
        ) : (
          <EmptyState title={t('timetable.noGroup')} />
        )
      ) : week.isError ? (
        <Failed error={week.error} />
      ) : week.isPending ? (
        <Loading />
      ) : blocks.length === 0 ? (
        <EmptyState
          title={t('timetable.empty')}
          {...(canWrite ? { description: t('timetable.empty.build') } : {})}
        />
      ) : (
        <TimetableGrid
          label={t('timetable.title')}
          blocks={blocks}
          primary={(block) => block.subjectName}
          secondary={(block) =>
            [block.teacherName ?? t('common.none'), block.classroomName].filter(Boolean).join(' · ')
          }
          highlighted={highlighted}
          {...(canWrite
            ? {
                onAdd: (at: Slot) => setDialog({ kind: 'add', slot: at }),
                onSelect: (block: TimetableBlock) => setDialog({ kind: 'edit', block }),
              }
            : {})}
        />
      )}
      <Modal
        open={formOpen}
        onClose={close}
        title={editing ? t('timetable.edit') : t('timetable.add')}
        {...(group ? { description: t('timetable.form.group', { group: group.name }) } : {})}
        size="lg"
      >
        {formOpen && group ? (
          <BlockForm
            groupId={group.id}
            block={editing}
            slot={slot}
            subjects={subjects.data?.items ?? []}
            teachers={teachers.data ?? []}
            classrooms={(classrooms.data?.items ?? []).filter(
              (classroom) => classroom.sedeId === sede.id,
            )}
            onSaved={(_saved, another) => {
              setHighlighted(new Set());
              toast.show({
                tone: 'success',
                title: editing ? t('timetable.saved') : t('timetable.created'),
              });
              void refresh();
              if (!another) close();
            }}
            onConflict={(conflicts) =>
              setHighlighted(new Set(conflicts.map((conflict) => conflict.block.id)))
            }
            {...(editing ? { onDelete: () => setDialog({ kind: 'delete', block: editing }) } : {})}
          />
        ) : null}
      </Modal>
      <ConfirmDialog
        open={dialog?.kind === 'delete'}
        title={t('timetable.delete.title')}
        body={dialog?.kind === 'delete' ? t('timetable.delete.body', words(dialog.block)) : ''}
        confirmLabel={t('timetable.delete')}
        cancelLabel={t('common.cancel')}
        busy={remove.isPending}
        error={deleteError}
        onConfirm={() => {
          if (dialog?.kind === 'delete') remove.mutate(dialog.block);
        }}
        onClose={close}
      />
      <Modal
        open={dialog?.kind === 'copy'}
        onClose={close}
        title={group ? t('timetable.copy.title', { group: group.name }) : t('timetable.copy')}
      >
        {dialog?.kind === 'copy' && group ? (
          <CopyForm
            target={group}
            targetCount={blocks.length}
            years={years.data?.items ?? []}
            groups={(groups.data?.items ?? []).filter((item) => item.sedeId === sede.id)}
            onDone={(result, from) => {
              close();
              setHighlighted(new Set());
              setOutcome({ result, from: from.name });
              void refresh();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

/**
 * A teacher's week of the year across their groups: read-only, since blocks belong to groups.
 */
const TeacherWeek = ({ yearId }: { yearId: string }) => {
  const t = useT();
  const api = useApi();
  const teachers = useQuery(recordQueries.teachers(api));
  const items = teachers.data ?? [];
  const [teacherId, setTeacherId] = useState('');
  const teacher = items.find((candidate) => candidate.id === teacherId) ?? items[0];
  const week = useQuery(timetableQueries.teacher(api, teacher?.id ?? '', yearId));
  const blocks = week.data?.blocks ?? [];
  if (teachers.isError) return <Failed error={teachers.error} />;
  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <Select
          label={t('timetable.filter.teacher')}
          value={teacher?.id ?? ''}
          onChange={(event) => setTeacherId(event.target.value)}
          options={items.map((item) => ({ value: item.id, label: teacherName(item) }))}
          placeholder={items.length === 0 ? t('timetable.noTeacher') : undefined}
        />
      </div>
      {!teacher ? (
        teachers.isPending ? (
          <Loading />
        ) : (
          <EmptyState title={t('timetable.noTeacher')} />
        )
      ) : week.isError ? (
        <Failed error={week.error} />
      ) : week.isPending ? (
        <Loading />
      ) : blocks.length === 0 ? (
        <EmptyState title={t('timetable.empty.teacher')} />
      ) : (
        <TimetableGrid
          label={t('timetable.teacher.week', { name: teacherName(teacher) })}
          blocks={blocks}
          primary={(block) => block.groupName}
          secondary={(block) =>
            [block.subjectName, block.classroomName].filter(Boolean).join(' · ')
          }
        />
      )}
    </div>
  );
};

/**
 * A classroom's occupancy in the year: which group has class there, when, with whom.
 */
const ClassroomWeek = ({ yearId }: { yearId: string }) => {
  const t = useT();
  const api = useApi();
  const { sede } = useTenant();
  const classrooms = useQuery(listQuery(api, 'classrooms', { sedeId: sede.id }));
  const items = (classrooms.data?.items ?? []).filter((classroom) => classroom.sedeId === sede.id);
  const [classroomId, setClassroomId] = useState('');
  const classroom = items.find((candidate) => candidate.id === classroomId) ?? items[0];
  const week = useQuery(timetableQueries.classroom(api, classroom?.id ?? '', yearId));
  const blocks = week.data?.blocks ?? [];
  if (classrooms.isError) return <Failed error={classrooms.error} />;
  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <Select
          label={t('timetable.filter.classroom')}
          value={classroom?.id ?? ''}
          onChange={(event) => setClassroomId(event.target.value)}
          options={items.map((item) => ({ value: item.id, label: item.name }))}
          placeholder={items.length === 0 ? t('timetable.noClassroom') : undefined}
        />
      </div>
      {!classroom ? (
        classrooms.isPending ? (
          <Loading />
        ) : (
          <EmptyState title={t('timetable.noClassroom')} />
        )
      ) : week.isError ? (
        <Failed error={week.error} />
      ) : week.isPending ? (
        <Loading />
      ) : blocks.length === 0 ? (
        <EmptyState title={t('timetable.empty.classroom')} />
      ) : (
        <TimetableGrid
          label={t('timetable.classroom.week', { name: classroom.name })}
          blocks={blocks}
          primary={(block) => block.groupName}
          secondary={(block) =>
            [block.subjectName, block.teacherName ?? t('common.none')].filter(Boolean).join(' · ')
          }
        />
      )}
    </div>
  );
};

/**
 * The timetable (scoping 3.6, #113): a year, then the week per group (the editor for the holders
 * of `timetable:write`), per teacher and per classroom. Read-only for everyone else.
 */
export const TimetableScreen = () => {
  const t = useT();
  const api = useApi();
  const { session } = useTenant();
  const canWrite = can(session, 'timetable:write');
  const years = useQuery(listQuery(api, 'years'));
  const yearItems = years.data?.items ?? [];
  const [yearId, setYearId] = useState('');
  const year = yearItems.find((candidate) => candidate.id === yearId) ?? defaultYear(yearItems);
  const [view, setView] = useState<TimetableViewKind>('group');
  const tabs = [
    {
      id: 'group',
      label: t('timetable.view.group'),
      content: year ? <GroupWeek yearId={year.id} canWrite={canWrite} /> : null,
    },
    ...(can(session, 'teachers:read')
      ? [
          {
            id: 'teacher',
            label: t('timetable.view.teacher'),
            content: year ? <TeacherWeek yearId={year.id} /> : null,
          },
        ]
      : []),
    ...(can(session, 'classrooms:read')
      ? [
          {
            id: 'classroom',
            label: t('timetable.view.classroom'),
            content: year ? <ClassroomWeek yearId={year.id} /> : null,
          },
        ]
      : []),
  ];
  return (
    <Card title={t('timetable.title')}>
      <div className="mb-4 max-w-xs">
        <Select
          label={t('timetable.filter.year')}
          value={year?.id ?? ''}
          onChange={(event) => setYearId(event.target.value)}
          options={yearItems.map((item) => ({
            value: item.id,
            label: yearLabel(item, t(`years.status.${item.status}`)),
          }))}
          placeholder={yearItems.length === 0 ? t('timetable.noYear') : undefined}
        />
      </div>
      {years.isError ? (
        <Failed error={years.error} />
      ) : years.isPending ? (
        <Loading />
      ) : !year ? (
        <EmptyState title={t('timetable.noYear')} />
      ) : (
        <Tabs
          items={tabs}
          value={view}
          onChange={(id) => setView(id === 'teacher' || id === 'classroom' ? id : 'group')}
          label={t('timetable.views')}
        />
      )}
    </Card>
  );
};
